import React from 'react';

interface AvatarProps {
  src?: string | null;
  alt?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xl2';
  className?: string;
  birthday?: string | null;
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = 'User avatar',
  size = 'md',
  className = '',
  birthday,
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-12 h-12 text-base',
    lg: 'w-16 h-16 text-lg',
    xl: 'w-20 h-20 text-xl',
    xl2: 'w-24 h-24 text-2xl',
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isBirthdayToday = (birthDate: string | null): boolean => {
    if (!birthDate) return false;

    try {
      const birth = new Date(birthDate);
      const today = new Date();

      return (
        birth.getMonth() === today.getMonth() &&
        birth.getDate() === today.getDate()
      );
    } catch {
      return false;
    }
  };

  const baseClasses = `
    rounded-full 
    flex 
    items-center 
    justify-center 
    font-medium 
    bg-gray-200 
    text-gray-700 
    overflow-hidden
    ${sizeClasses[size]}
    ${className}
  `;

  const showBirthdayBadge = isBirthdayToday(birthday ?? null);

  if (src) {
    return (
      <div className="relative">
        <div className={baseClasses}>
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to initials if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `<span>${getInitials(alt)}</span>`;
              }
            }}
          />
        </div>
        {showBirthdayBadge && (
          <div className="absolute -top-1 -left-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
            <span className="text-sm">🎂</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative">
      <div className={baseClasses}>
        <span>{getInitials(alt)}</span>
      </div>
      {showBirthdayBadge && (
        <div className="absolute -top-1 -left-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
          <span className="text-sm">🎂</span>
        </div>
      )}
    </div>
  );
};

export default Avatar;
